import { FC, ReactNode } from "react";

import { Box, Skeleton, Stack, useTheme } from "@mui/material";
import NextHead from "next/head";
import NextLink from "next/link";

import { ProgramCard } from "../components/ProgramCard";
import { PastRecipients } from "../GrantProgramDetail/PastRecipients/PastRecipients";
import { cleanText, formatHistoricalAmount } from "../helpers/formatters";

import { trpc } from "@/common/helpers/trpc";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

interface FoundationProfileProps {
  ein?: string;
  funderId?: string;
}

const PROGRAMS_PAGE_SIZE = 9;

const FUNDER_TYPE_TAG: Record<string, string> = {
  PRIVATE_FOUNDATION: "Foundation",
  FED_AGENCY: "Federal agency",
  FED_SUBAGENCY: "Federal sub-agency",
  STATE_AGENCY: "State agency",
};

export const FoundationProfile: FC<FoundationProfileProps> = ({ ein, funderId }) => {
  const theme = useTheme();

  const byEin = trpc.grantFinder_getFunderByEin.useQuery({ ein: ein ?? "" }, { enabled: Boolean(ein) });
  const byId = trpc.grantFinder_getFunderById.useQuery({ funderId: funderId ?? "" }, { enabled: Boolean(funderId) });

  const funder = ein ? byEin.data : byId.data;
  const isFunderLoading = ein ? byEin.isLoading : byId.isLoading;
  const isFunderError = ein ? byEin.isError : byId.isError;

  const { data: programs, isLoading: isProgramsLoading } = trpc.grantFinder_listGrantPrograms.useQuery(
    {
      page: 0,
      pageSize: PROGRAMS_PAGE_SIZE,
      activeOnly: false,
      funderId: funder?.FUNDER_ID ?? "",
    },
    {
      enabled: Boolean(funder?.FUNDER_ID),
    }
  );

  if (isFunderLoading) {
    return (
      <Box sx={{ maxWidth: 1024, mx: "auto", px: { xs: 2, md: 4 }, py: { xs: 4, md: 6 } }}>
        <Stack spacing={3}>
          <Skeleton variant="text" width={140} />
          <Skeleton variant="text" height={48} />
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
        </Stack>
      </Box>
    );
  }

  if (isFunderError || !funder) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", px: 2, py: { xs: 6, md: 10 }, textAlign: "center" }}>
        <Stack spacing={2} alignItems="center">
          <DashboardTypography variant="h3" vibe="text-neutral-intense">
            Funder not found
          </DashboardTypography>
          <DashboardTypography variant="body1" vibe="text-neutral-moderate">
            {ein
              ? `We don't have a record of EIN ${ein}. Try the search.`
              : "We don't have a record of this funder. Try the search."}
          </DashboardTypography>
          <DashboardButton href="/grant-finder" variant="filled" vibe="brand">
            Browse open grants
          </DashboardButton>
        </Stack>
      </Box>
    );
  }

  const foundationPrograms = programs?.items ?? [];
  const location = [funder.CITY, funder.STATE].filter(Boolean).join(", ");
  const about = funder.FUNDER_DESCRIPTION ?? funder.SITE_SUMMARY;
  const isFoundation = funder.FUNDER_TYPE === "PRIVATE_FOUNDATION";
  const funderNoun = isFoundation ? "foundation" : "funder";
  const typeTag = funder.FUNDER_TYPE ? (FUNDER_TYPE_TAG[funder.FUNDER_TYPE] ?? "Funder") : "Funder";
  const cleanedName = cleanText(funder.NAME);
  const dynamicTitle = cleanedName ? `${cleanedName} | Zeffy Grant Finder` : "Funder | Zeffy Grant Finder";
  const funderLabel = cleanedName || `this ${funderNoun}`;
  const dynamicDescription = cleanText(about ? about.slice(0, 220) : `Open grant programs from ${funderLabel}.`);
  const ogSubtitle = location ? `${typeTag} • ${location}` : typeTag;
  const ogImageUrl = `/api/grant-finder/og?title=${encodeURIComponent(cleanedName || "Foundation")}&subtitle=${encodeURIComponent(ogSubtitle)}`;

  return (
    <>
      <NextHead>
        <title key="title">{dynamicTitle}</title>
        <meta key="description" name="description" content={dynamicDescription} />
        <meta key="og:title" property="og:title" content={dynamicTitle} />
        <meta key="og:description" property="og:description" content={dynamicDescription} />
        <meta key="og:image" property="og:image" content={ogImageUrl} />
      </NextHead>

      <Box sx={{ maxWidth: 1024, mx: "auto", px: { xs: 2, md: 4 }, py: { xs: 4, md: 6 } }}>
        <Stack spacing={4}>
          <Box
            component={NextLink}
            href="/grant-finder"
            sx={{ display: "inline-flex", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            <DashboardTypography variant="body2" sx={{ color: "#3F3FA8", fontWeight: 500 }}>
              ← Back to search
            </DashboardTypography>
          </Box>

          <Stack direction="row" spacing={2.5} alignItems="center">
            {funder.LOGO_URL ? (
              <Box
                component="img"
                src={funder.LOGO_URL}
                alt={`${funder.NAME ?? "Foundation"} logo`}
                sx={{
                  width: 64,
                  height: 64,
                  objectFit: "contain",
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.common.white,
                  p: 0.5,
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Stack spacing={1}>
              <DashboardTypography variant="h3" vibe="text-neutral-moderate">
                {funder.NAME ? cleanText(funder.NAME) : "Unnamed foundation"}
              </DashboardTypography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Tag>{typeTag}</Tag>
                {funder.EIN ? <Tag>{`EIN: ${formatEin(funder.EIN)}`}</Tag> : null}
              </Stack>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
              gap: { xs: 4, md: 5 },
              alignItems: "start",
            }}
          >
            <Stack spacing={4} sx={{ minWidth: 0 }}>
              {about ? (
                <Section title={`About this ${funderNoun}`}>
                  <DashboardTypography variant="body1" vibe="text-neutral-moderate" sx={{ whiteSpace: "pre-wrap" }}>
                    {cleanText(about)}
                  </DashboardTypography>
                </Section>
              ) : null}

              {funder.FUNDING_FOCUS_STATEMENT ? (
                <Section title="Funding focus">
                  <DashboardTypography variant="body1" vibe="text-neutral-moderate" sx={{ whiteSpace: "pre-wrap" }}>
                    {cleanText(funder.FUNDING_FOCUS_STATEMENT)}
                  </DashboardTypography>
                  {funder.FUNDER_TOPICS && funder.FUNDER_TOPICS.length > 0 ? (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                      {funder.FUNDER_TOPICS.map((topic) => (
                        <Tag key={topic}>{topic}</Tag>
                      ))}
                    </Stack>
                  ) : null}
                </Section>
              ) : null}

              {funder.FUNDER_ID ? <PastRecipients funderId={funder.FUNDER_ID} title="Past grantees" /> : null}

              <Section title="Grant programs">
                {isProgramsLoading ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                      gap: 1.5,
                    }}
                  >
                    {Array.from({ length: 2 }).map((_, idx) => (
                      <Skeleton key={idx} variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                    ))}
                  </Box>
                ) : foundationPrograms.length === 0 ? (
                  <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                    No tracked programs from this {funderNoun}. Check{" "}
                    {funder.GRANTS_PAGE_URL ? (
                      <Box
                        component="a"
                        href={funder.GRANTS_PAGE_URL}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ color: "#3F3FA8", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                      >
                        their grants page
                      </Box>
                    ) : (
                      "the funder website"
                    )}{" "}
                    for upcoming cycles.
                  </DashboardTypography>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                      gap: 1.5,
                    }}
                  >
                    {foundationPrograms.map((p) => (
                      <ProgramCard
                        key={p.PROGRAM_ID}
                        programId={p.PROGRAM_ID}
                        title={p.TITLE}
                        funderName={p.FUNDER_NAME}
                        funderType={p.FUNDER_TYPE}
                        category={p.CATEGORY}
                        causes={p.CAUSES}
                        fundingActivityCategories={p.FUNDING_ACTIVITY_CATEGORIES}
                        assistanceListings={p.CFDA_LIST}
                        awardFloor={p.AWARD_FLOOR}
                        awardCeiling={p.AWARD_CEILING}
                        historicalMedian={p.FUNDER_MEDIAN_HISTORICAL_GRANT_AMOUNT}
                        deadline={p.DT_CLOSE}
                        openDate={p.DT_OPEN}
                        eligibleStates={p.ELIGIBLE_STATES}
                        geographyScope={p.ELIGIBLE_GEOGRAPHY_SCOPE}
                        lifecycleStatus={p.LIFECYCLE_STATUS}
                      />
                    ))}
                  </Box>
                )}
              </Section>
            </Stack>

            <Box
              sx={{
                backgroundColor: theme.palette.common.white,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                boxShadow: "0 1px 2px rgba(15,16,36,0.04)",
                p: { xs: 2.5, md: 3 },
                position: { md: "sticky" },
                top: { md: 24 },
              }}
            >
              <Stack spacing={2.5}>
                <DashboardTypography variant="h5" vibe="text-neutral-moderate">
                  {isFoundation ? "Foundation Details" : "Funder Details"}
                </DashboardTypography>
                {location ? <Detail label="Location" value={location} /> : null}
                {funder.FUNDER_AREAS_SERVED && funder.FUNDER_AREAS_SERVED.length > 0 ? (
                  <Detail label="Areas served" value={funder.FUNDER_AREAS_SERVED.join(", ")} />
                ) : null}
                {funder.FUNDER_TOPICS && funder.FUNDER_TOPICS.length > 0 ? (
                  <Detail label="Causes served" value={funder.FUNDER_TOPICS.join(", ")} />
                ) : null}
                {formatHistoricalAmount(funder.MEDIAN_HISTORICAL_GRANT_AMOUNT) ? (
                  <Detail
                    label="Typical grant amount"
                    value={formatHistoricalAmount(funder.MEDIAN_HISTORICAL_GRANT_AMOUNT) as string}
                  />
                ) : null}
                {funder.N_PROGRAMS_ACTIVE && funder.N_PROGRAMS_ACTIVE > 0 ? (
                  <Detail label="Open programs" value={funder.N_PROGRAMS_ACTIVE.toLocaleString()} />
                ) : null}
                {(funder.WEBSITE || funder.GRANTS_PAGE_URL) && (
                  <Stack spacing={1} sx={{ pt: 0.5 }}>
                    {funder.GRANTS_PAGE_URL ? (
                      <DashboardButton
                        href={funder.GRANTS_PAGE_URL}
                        target="_blank"
                        variant="filled"
                        vibe="brand"
                        fullWidth
                      >
                        See grant programs →
                      </DashboardButton>
                    ) : null}
                    {funder.WEBSITE ? (
                      <DashboardButton
                        href={funder.WEBSITE}
                        target="_blank"
                        variant="outlined"
                        vibe="neutral"
                        fullWidth
                      >
                        Visit website
                      </DashboardButton>
                    ) : null}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
    </>
  );
};

const formatEin = (ein: string): string => (ein.length === 9 ? `${ein.slice(0, 2)}-${ein.slice(2)}` : ein);

interface TagProps {
  children: ReactNode;
}

const Tag: FC<TagProps> = ({ children }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.25,
      borderRadius: 999,
      backgroundColor: "rgba(85,85,231,0.10)",
      whiteSpace: "nowrap",
    }}
  >
    <DashboardTypography variant="caption" sx={{ color: "#3F3FA8", fontWeight: 500 }}>
      {children}
    </DashboardTypography>
  </Box>
);

interface SectionProps {
  title: string;
  children: ReactNode;
}

const Section: FC<SectionProps> = ({ title, children }) => {
  const theme = useTheme();
  return (
    <Stack spacing={1.5} sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
      <DashboardTypography variant="h5" vibe="text-neutral-moderate">
        {title}
      </DashboardTypography>
      {children}
    </Stack>
  );
};

interface DetailProps {
  label: string;
  value: string;
}

const Detail: FC<DetailProps> = ({ label, value }) => (
  <Stack spacing={0.25}>
    <DashboardTypography variant="caption" vibe="text-neutral-quiet">
      {label}
    </DashboardTypography>
    <DashboardTypography variant="subtitle2" vibe="text-neutral-intense">
      {value}
    </DashboardTypography>
  </Stack>
);
